import { useThreadStore } from "@/store";
import {
  parseToolCalls,
  hasCompletedToolCalls as checkCompletedToolCalls,
} from "./parsingUtils";
import {
  isFirecrawlSearchTool,
  isImageGenerationTool,
  isWebSearchTool,
} from "./toolHelpers";

// Helper function to extract clean file name from possible metadata format
export const extractCleanFileName = (fileName) => {
  if (!fileName) return "Unknown file";

  // Check for origname format (origname=ACTUAL_FILENAME.ext)
  const orignameMatch = fileName.match(/origname=([^&]+)/);
  if (orignameMatch && orignameMatch[1]) {
    return decodeURIComponent(orignameMatch[1]);
  }

  // Remove timestamp and random IDs commonly found in uploaded files
  // Example: 1737254657048-CREATING_TOOLS.md
  const timestampMatch = fileName.match(/^\d+-(.+)$/);
  if (timestampMatch && timestampMatch[1]) {
    fileName = timestampMatch[1];
  }

  // Remove numerical ID patterns from the end of filenames
  // Example: CREATING_TOOLS_1744455203921_562897.md -> CREATING_TOOLS.md
  const fileNameParts = fileName.split(".");
  const extension = fileNameParts.length > 1 ? `.${fileNameParts.pop()}` : "";
  let baseName = fileNameParts.join(".");

  // Comprehensive ID pattern removal:
  // 1. Remove patterns like _1234567890 or _1234-5678 at the end
  baseName = baseName.replace(/(_\d+(-\d+)*)+$/, "");
  // 2. Also handle hyphenated IDs like -1234567890 at the end
  baseName = baseName.replace(/(-\d+)+$/, "");
  // 3. Handle UUID-style patterns (8-4-4-4-12 hex digits)
  baseName = baseName.replace(
    /_[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12}$/i,
    ""
  );
  // 4. Handle double underscores sometimes left behind
  baseName = baseName.replace(/__+$/, "");
  // 5. Remove any trailing underscores or hyphens
  baseName = baseName.replace(/[_-]+$/, "");

  return baseName + extension;
};

/**
 * Process text content from a message object
 */
export function processTextContent(message) {
  const content = message?.content;
  if (!content) return "";

  if (typeof content === "string") return content;

  if (Array.isArray(content)) {
    return content.reduce((acc, item) => {
      if (item.type === "text") {
        if (typeof item.text === "string") return acc + item.text;
        if (item.text?.value) return acc + item.text.value;
      }
      return acc;
    }, "");
  }

  if (typeof content === "object") {
    return content?.text?.value || content?.text || JSON.stringify(content);
  }

  return String(content);
}

/**
 * Extract tool calls from a message
 * @param {Object} message - The message object
 * @returns {Object} - Object with hasToolCalls boolean and toolCalls array
 */
export function extractToolCalls(message) {
  const toolCalls = parseToolCalls(message);
  return {
    hasToolCalls: toolCalls.length > 0,
    toolCalls,
  };
}

/**
 * Check if a message should be considered streaming
 * @param {Object} message - The message object
 * @returns {boolean} - True if message is actively streaming
 */
export function isMessageStreaming(message) {
  const { currentMessageId, messages } = useThreadStore.getState();
  const isCurrentMessage = message.id === currentMessageId;

  // Check if message has completed tool calls
  const toolCalls = parseToolCalls(message);
  if (toolCalls.length > 0 && checkCompletedToolCalls(toolCalls)) {
    return false;
  }

  // For deep research messages, check if there are newer messages after this one
  if (message.metadata?.isDeepResearchInProgress) {
    const messageIndex = messages.findIndex((msg) => msg.id === message.id);
    if (messageIndex !== -1) {
      // Check if there are any messages after this one
      const hasNewerMessages = messageIndex < messages.length - 1;
      if (hasNewerMessages) {
        return false; // Stop pulsing if there are newer messages
      }
    }
  }

  // Only consider a message streaming if it's the current message being streamed
  return message.isStreaming === true && isCurrentMessage;
}

/**
 * Check if a message should show in streaming component vs main messages
 * @param {Object} message - The message object
 * @returns {boolean} - True if should show in StreamingMessage component
 */
export function shouldShowInStreamingMessage(message) {
  // Must be actively streaming
  if (message.isStreaming !== true) {
    return false;
  }

  // If it has completed tool calls with results, don't show in StreamingMessage
  // (it should be shown in the main Messages component instead)
  const toolCalls = parseToolCalls(message);
  if (toolCalls.length > 0 && checkCompletedToolCalls(toolCalls)) {
    return false;
  }

  return true; // This is an actively streaming message without completed results
}

/**
 * Get tool name for skeleton rendering
 */
export function getToolNameForSkeleton(
  toolCalls,
  currentToolCallName,
  toolType
) {
  if (toolType === "image") {
    const imageCall = toolCalls?.find((call) =>
      isImageGenerationTool(call.function?.name || call.name)
    );
    return (
      imageCall?.function?.name ||
      imageCall?.name ||
      currentToolCallName ||
      "image_generation"
    );
  }

  return currentToolCallName;
}

/**
 * Check if should render tool skeleton for a message
 */
export function shouldRenderToolSkeleton(
  isUser,
  skipToolCalls,
  effectiveIsStreaming,
  messageId,
  hasToolCalls,
  toolCalls,
  currentToolCallName,
  toolType
) {
  if (isUser || skipToolCalls) return false;

  const { currentMessageId } = useThreadStore.getState();
  const isCurrentStreamingMessage = messageId === currentMessageId;

  // Only show skeletons for the currently streaming message
  if (effectiveIsStreaming && !isCurrentStreamingMessage) {
    return false;
  }

  // Show skeleton during streaming for specific tool types
  if (effectiveIsStreaming && hasToolCalls) {
    if (toolType === "image") {
      return toolCalls.some((call) =>
        isImageGenerationTool(call.function?.name || call.name)
      );
    }
    if (toolType === "web") {
      return toolCalls.some(
        (call) =>
          isFirecrawlSearchTool(call.function?.name || call.name) ||
          isWebSearchTool(call.function?.name || call.name)
      );
    }
  }

  // Show skeleton for streaming tool calls that haven't populated yet
  if (
    effectiveIsStreaming &&
    !hasToolCalls &&
    currentToolCallName &&
    isCurrentStreamingMessage
  ) {
    if (toolType === "image") {
      return isImageGenerationTool(currentToolCallName);
    }
    if (toolType === "web") {
      return (
        isFirecrawlSearchTool(currentToolCallName) ||
        isWebSearchTool(currentToolCallName)
      );
    }
  }

  return false;
}
